import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { twMerge } from 'tailwind-merge';
import { TFunction } from 'i18next';
import type { Option } from 'components/Forms/types';
import { ConnectedFormInputProps } from 'types/form-field';
import { getTranslatedSelectOptionLabel } from 'domains/language/options';

type Props = {
  id: string;
  option: Option;
  align?: 'left' | 'center' | 'right';
  highlightSelected?: boolean;
  hasError?: boolean;

  tPrefixKey?: string;
  tFunc?: TFunction;
} & Omit<ConnectedFormInputProps<HTMLInputElement>, 'label' | 'clearValue' | 'setError' | 'setValue'>;

export const RadioCardInput = React.forwardRef(function RadioCardInputWithRef(
  {
    id,
    name,
    option,
    align = 'center',
    hasError,
    highlightSelected = true,
    disabled = false,
    defaultChecked,
    className,
    labelClassName,
    tFunc,
    tPrefixKey,
    ...inputProps
  }: Props,
  ref?: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  const inputsRef = useRef<NodeList>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState<boolean>(!!defaultChecked);

  const { value, description, startAdornment } = option;

  /**
   * @NOTE(shawk): We use this approach instead of mirroring input value in
   * react state since react-hook-form controls this input and it operates in
   * an uncontrolled fashion. If we just attach `onChange` to this input it
   * doesn't fire when a different input in the group is checked.
   */
  useEffect(() => {
    const el = inputRef.current;

    if (!inputsRef.current) {
      inputsRef.current = document.querySelectorAll(`input[type="radio"][name="${name}"]`);
    }

    if (el?.checked) {
      setChecked(true);
    }

    const onChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      setChecked(e.target === el && target.checked);
    };

    inputsRef.current.forEach(input => {
      input.addEventListener('change', onChange);
    });

    return () => {
      inputsRef.current?.forEach(input => {
        input.removeEventListener('change', onChange);
      });
    };
  }, [name, inputsRef, inputRef]);

  return (
    <label
      htmlFor={id}
      className={cx('relative block w-full peer-checked:bg-primary-100', {
        'cursor-pointer': !disabled,
      })}
    >
      <input
        {...inputProps}
        ref={composeRefs(inputRef, ref)}
        id={id}
        type="radio"
        name={name}
        className="peer sr-only"
        value={value}
        disabled={disabled}
        defaultChecked={defaultChecked}
      />

      <span
        className={cx(
          'flex items-center rounded-lg py-16  px-16 min-h-[56px] leading-none transition-all',
          'text-black bg-white border border-black-300 ring-inset font-dazzed',
          {
            'justify-start': align === 'left',
            'justify-center': align === 'center',
            'justify-end': align === 'right',
            'peer-hover:bg-purple-50 peer-hover:border-black-900 peer-checked:border-black-900 peer-checked:ring-2 peer-checked:ring-black-900 peer-checked:bg-primary-100 hover:bg-primary-50':
              !disabled,
            'peer-checked:bg-primary-100': !disabled && highlightSelected,
            'hover:bg-white text-gray-400 peer-checked:ring-1 peer-checked:border-gray-500 peer-checked:ring-gray-500':
              disabled,
            'border-error-900': hasError,
          },
          className,
        )}
      >
        {startAdornment && (
          <div className="mr-12 mb-auto">
            {typeof startAdornment === 'function' ? startAdornment(checked) : startAdornment}
          </div>
        )}

        <div className="flex flex-col space-y-6">
          <span className={twMerge(cx('font-medium text-sm md:text-md', labelClassName))}>
            {getTranslatedSelectOptionLabel({ label: `${option.label}`, value: option.value }, tFunc, tPrefixKey)}
          </span>

          {description && (
            <span
              className={cx('leading-normal text-xs md:text-sm', {
                'text-inherit': disabled,
                'text-gray-600': !disabled && !hasError,
                'text-error-900': !disabled && hasError,
              })}
            >
              {description}
            </span>
          )}
        </div>
      </span>

      <span
        className={cx(
          'absolute inset-0 rounded-md outline-none ring-offset-0 transition-all',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/30 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white',
        )}
      />
    </label>
  );
});
